import React, { useEffect, useState } from "react";
import { Button, Drawer, Form, Input, InputNumber, Select, Switch, Table, message } from "antd";
import type { ColumnsType } from "antd/es/table";
import { TokenService, TaskService } from "../../services";
import { TokenConfig, CreateTokenConfigDto, UpdateTokenConfigDto, TaskTypeOption } from "../../types/token";
import ErrorHandler from "../../utils/errorHandler";
import MainLayout from "../../components/layout/MainLayout";
import { DrawerFooter, EmptyState, Icon, StatusBadge, Surface, TASK_TYPE_LABEL, formatTime } from "../../components/ui";
import "./TokenConfig.scss";

const RULES: { title: string; desc: string }[] = [
  { title: "创建前校验", desc: "按商品数 × 单次消耗计算所需算力；24 小时内已扣过的商品不重复计算，余额不足时拒绝创建。" },
  { title: "成功才扣除", desc: "任务成功完成时写入一条「消耗」流水；失败、取消与过期的任务不扣除。" },
  { title: "附加项另计", desc: "裂变中的「抄同行标题 / 主图」各消耗 10 算力，在桌面端提交前提示。" },
];

const TokenConfigPage: React.FC = () => {
  const [configs, setConfigs] = useState<TokenConfig[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<TokenConfig | null>(null);
  const [taskTypes, setTaskTypes] = useState<TaskTypeOption[]>([]);
  const [form] = Form.useForm();

  const loadConfigs = async () => {
    setLoading(true);
    try {
      const response = await TokenService.getAllConfigs();
      if (response.code === 0 && response.data) setConfigs(response.data);
      else message.error(response.msg || "获取算力配置失败");
    } catch (error) {
      ErrorHandler.handleApiError(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConfigs();
    TaskService.getTaskTypeList()
      .then((response) => {
        if (response.code === 0 && response.data) setTaskTypes(response.data.types);
      })
      .catch((error) => ErrorHandler.handleApiError(error));
  }, []);

  const typeLabel = (value: string) => TASK_TYPE_LABEL[value] || taskTypes.find((option) => option.value === value)?.label || value;

  const openDrawer = (config?: TokenConfig) => {
    setEditing(config || null);
    form.resetFields();
    if (config) form.setFieldsValue(config);
    else form.setFieldsValue({ isEnabled: true });
    setDrawerOpen(true);
  };

  const handleSave = async (values: CreateTokenConfigDto | UpdateTokenConfigDto) => {
    setSaving(true);
    try {
      const response = editing ? await TokenService.updateConfig(editing.id, values) : await TokenService.createConfig(values as CreateTokenConfigDto);
      if (response.code === 0) {
        message.success(`${editing ? "更新" : "创建"}配置成功`);
        setDrawerOpen(false);
        loadConfigs();
      } else {
        message.error(response.msg || "操作失败");
      }
    } catch (error) {
      ErrorHandler.handleApiError(error);
    } finally {
      setSaving(false);
    }
  };

  const columns: ColumnsType<TokenConfig> = [
    {
      title: "任务类型",
      dataIndex: "taskType",
      key: "taskType",
      width: 210,
      render: (value: string) => (
        <span className="ddp-two-line">
          <strong className="pricing-page__type">{typeLabel(value)}</strong>
          <small className="mono">{value}</small>
        </span>
      ),
    },
    {
      title: "单次消耗",
      dataIndex: "tokenCost",
      key: "tokenCost",
      width: 124,
      render: (cost: number) => (
        <span className="pricing-page__cost">
          <strong>{cost}</strong>
          <small>算力 / 次</small>
        </span>
      ),
    },
    { title: "说明", dataIndex: "description", key: "description", ellipsis: true, render: (desc?: string) => desc || <span className="text-faint">—</span> },
    {
      title: "状态",
      dataIndex: "isEnabled",
      key: "isEnabled",
      width: 92,
      render: (enabled: boolean) => <StatusBadge tone={enabled ? "sage" : "ink"}>{enabled ? "启用" : "停用"}</StatusBadge>,
    },
    { title: "更新时间", dataIndex: "updatedAt", key: "updatedAt", width: 156, render: (time: string, record) => <span className="mono text-muted">{formatTime(time || record.createdAt)}</span> },
    {
      title: "操作",
      key: "action",
      width: 64,
      align: "right",
      render: (_, record) => (
        <button type="button" className="ddp-link" onClick={() => openDrawer(record)}>
          编辑
        </button>
      ),
    },
  ];

  const typeOptions = taskTypes.map((option) => ({ value: option.value, label: `${typeLabel(option.value)} · ${option.value}` }));

  return (
    <MainLayout>
      <div className="pricing-page">
        <Surface
          className="pricing-page__surface"
          toolbar={
            <>
              <span className="pricing-page__count">
                共 <b className="mono">{configs.length}</b> 类任务
              </span>
              <Button type="primary" icon={<Icon name="plus" />} onClick={() => openDrawer()}>
                新增配置
              </Button>
            </>
          }
        >
          <Table
            columns={columns}
            dataSource={configs}
            rowKey="id"
            loading={loading}
            pagination={false}
            scroll={{ x: 760 }}
            locale={{
              emptyText: (
                <EmptyState
                  icon="sliders"
                  title="还没有算力配置"
                  desc="没有配置的任务类型不会扣除算力。先为常用任务设置单次消耗。"
                  action={<Button type="primary" onClick={() => openDrawer()}>新增配置</Button>}
                />
              ),
            }}
          />
        </Surface>

        <aside className="pricing-rules" aria-labelledby="pricing-rules-title">
          <h2 id="pricing-rules-title">扣费规则</h2>
          <ol>
            {RULES.map((rule, index) => (
              <li key={rule.title}>
                <span className="pricing-rules__index mono">{index + 1}</span>
                <div>
                  <strong>{rule.title}</strong>
                  <p>{rule.desc}</p>
                </div>
              </li>
            ))}
          </ol>
          <p className="pricing-rules__foot">
            <Icon name="info" />
            修改立即生效：扣费时按当时的配置计算。
          </p>
        </aside>
      </div>

      <Drawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        width={460}
        rootClassName="ddp-drawer"
        destroyOnClose
        title={
          <span className="ddp-drawer-title">
            <span>{editing ? "编辑算力配置" : "新增算力配置"}</span>
            <span className="ddp-drawer-title__sub">{editing ? typeLabel(editing.taskType) : "为一类任务设置单次消耗的算力"}</span>
          </span>
        }
        footer={
          <DrawerFooter>
            <Button onClick={() => setDrawerOpen(false)}>取消</Button>
            <Button type="primary" loading={saving} onClick={() => form.submit()}>
              {editing ? "保存修改" : "创建配置"}
            </Button>
          </DrawerFooter>
        }
      >
        <Form form={form} layout="vertical" onFinish={handleSave}>
          <Form.Item name="taskType" label="任务类型" rules={[{ required: true, message: "请选择任务类型" }]}>
            <Select placeholder="请选择任务类型" options={typeOptions} showSearch optionFilterProp="label" disabled={!!editing} />
          </Form.Item>
          <Form.Item
            name="tokenCost"
            label="单次消耗"
            rules={[
              { required: true, message: "请输入消耗数量" },
              {
                validator: (_, value) => {
                  if (value === null || value === undefined || value === "") return Promise.reject(new Error("请输入消耗数量"));
                  const numValue = Number(value);
                  if (Number.isNaN(numValue)) return Promise.reject(new Error("请输入有效的数字"));
                  if (numValue < 0) return Promise.reject(new Error("消耗数量不能小于0"));
                  return Promise.resolve();
                },
              },
            ]}
          >
            <InputNumber placeholder="请输入消耗数量" style={{ width: "100%" }} min={0} precision={0} addonAfter="算力 / 次" />
          </Form.Item>
          <Form.Item name="description" label="说明">
            <Input.TextArea placeholder="例如：插件防比价报名，每件商品报名成功后计一次" rows={3} />
          </Form.Item>
          <Form.Item name="isEnabled" label="启用状态" valuePropName="checked" extra="停用后，该类任务不再按此配置扣费。">
            <Switch checkedChildren="启用" unCheckedChildren="停用" />
          </Form.Item>
        </Form>
      </Drawer>
    </MainLayout>
  );
};

export default TokenConfigPage;
